import React, { createContext, PropsWithChildren, useCallback, useContext, useMemo, useRef, useState } from "react";
import { useAppConfig } from "@ct-react/core";
import { ArticleType } from "@shared/url";

const sync = (name: string): Record<ArticleType, string[]> => {
  const empty = Object.values(ArticleType).reduce((acc, cur) => ({ ...acc, [cur]: [] }), {} as Record<ArticleType, string[]>);
  try {
    const content = localStorage.getItem(name);
    return !!content ? JSON.parse(content) as Record<ArticleType, string[]> : empty;
  } catch {
    return empty;
  }
};

type Props = {
  saves: Record<ArticleType, string[]>;
  toggle: (type: ArticleType, id: string) => void;
  check: (type: ArticleType, id: string) => boolean;
};

const BookmarksContext = createContext<Props | undefined>(undefined);

export function useBookmarksContext() {
  const ctx = useContext(BookmarksContext);
  if (!ctx) throw new Error("useBookmarksContext must be used within Bookm");
  return ctx;
}


export const BookmarksProvider = ({ children }: PropsWithChildren) => {

  const { id } = useAppConfig();
  const storageName = useRef<string>(`ct-${id}-bm`);

  const [ saves, setSaves ] = useState<Record<ArticleType, string[]>>(sync(storageName.current));

  const toggle = useCallback((type: ArticleType, id: string) => {
    setSaves(prev => {
      const updated = {
        ...prev,
        [type]: prev[type].includes(id) ? prev[type].filter(item => item !== id) : [ ...prev[type], id ]
      }
      localStorage.setItem(storageName.current, JSON.stringify(updated));
      return updated;
    })
  }, []);

  const check = useCallback((type: ArticleType, id: string) => saves[type].includes(id), [ saves ]);

  const value = useMemo(() => ({ saves, toggle, check }), [ saves ]);

  return (
    <BookmarksContext.Provider value={value}>
      {children}
    </BookmarksContext.Provider>);

}
